import React, { useEffect, useState } from "react"

import { SignOutButton } from "@clerk/clerk-react"
import IconButton from "@mui/material/IconButton"
import ListItemButton from "@mui/material/ListItemButton"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import { useTheme } from "@mui/material/styles"
import Stack from "@mui/system/Stack"
import { User, LogOut, MessageSquareText } from "lucide-react"
import { useTranslation } from "react-i18next"

import useSession from "@hooks/useSession"

import AnchorLink from "@components/AnchorLink"
import Avatar from "@components/Avatar"
import { IconWrapper } from "@components/IconWrapper"
import { useMenu } from "@components/Menu/useMenu"
import { useSnackbar } from "@providers/SnackbarProvider"

import routes from "@constants/routes"

const UserMenu: React.FC = () => {
  const { t } = useTranslation()
  const { orgMember, currentUser } = useSession()
  const { openSnackbar } = useSnackbar()
  const theme = useTheme()

  const [orgMemberImage, setOrgMemberImage] = useState(
    orgMember?.profile?.image || "",
  )
  const [showWidget, setShowWidget] = useState(false)

  const handleWidgetToggle = () => {
    if (showWidget) {
      // Widget is currently visible, so hide it
      window.HubSpotConversations?.widget?.remove()
      setShowWidget(false)
    } else {
      const hubspotIsLoaded = window.HubSpotConversations?.widget
      if (hubspotIsLoaded) {
        // Widget is not currently visible, so show it
        window.HubSpotConversations?.widget?.load()
        window.HubSpotConversations?.widget?.open()

        setShowWidget(true)
      } else {
        // User can't load hubspot for some reason, possibly an ad-blocker
        openSnackbar(t("appMenu_openError"), {
          severity: "error",
          title: t("snackbar:error.title"),
        })
      }
    }
  }

  useEffect(() => {
    setOrgMemberImage((prevImage) => {
      if (orgMember?.profile?.image) {
        return orgMember.profile.image
      }
      return prevImage
    })
  }, [orgMember?.profile?.image])

  const menuProps = useMenu()

  return (
    <>
      <IconButton
        aria-controls={menuProps.open ? "account-menu" : undefined}
        aria-label="Toggle user account menu"
        aria-expanded={menuProps.open ? "true" : undefined}
        aria-haspopup="true"
        data-cy="Drawer-UserMenu"
        id="account-button"
        onClick={menuProps.onClick}
      >
        <Avatar image={orgMemberImage} size={16} />
      </IconButton>

      <Menu
        anchorEl={menuProps.anchorEl}
        id="account-menu"
        open={menuProps.open}
        onClose={menuProps.onClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        sx={{
          transform: `translateX(${theme.spacing(1)})`,
        }}
      >
        <MenuItem>
          <AnchorLink
            url={`${routes.USERS}/${currentUser?.id}`}
            sx={{
              width: "100%",
            }}
            onClick={menuProps.onClose}
          >
            <Stack direction="row" alignItems="center" sx={{ gap: 1 }}>
              <IconWrapper color={() => "inherit"}>
                <User />
              </IconWrapper>

              {t("appMenu_profile")}
            </Stack>
          </AnchorLink>
        </MenuItem>

        <MenuItem onClick={handleWidgetToggle} divider sx={{ gap: 1 }}>
          <IconWrapper color={() => "inherit"}>
            <MessageSquareText />
          </IconWrapper>
          {showWidget ? t("appMenu_closeChat") : t("appMenu_support")}
        </MenuItem>

        <MenuItem>
          <SignOutButton
            // @ts-expect-error ClerkJS has wrong prop types, this works
            style={{
              padding: 0,
              backgroundColor: "transparent",
              color: "inherit",
            }}
          >
            <ListItemButton data-cy="Logout-Button">
              <IconWrapper color={() => "inherit"}>
                <LogOut />
              </IconWrapper>
              {t("appMenu_signOut")}
            </ListItemButton>
          </SignOutButton>
        </MenuItem>
      </Menu>
    </>
  )
}

export default UserMenu
